import React, { useEffect, useState, useRef } from 'react'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import {
    Paper,
    Grid,
    Typography,
    Zoom,
    Table,
    TableBody,
    TableHead,
    TableContainer,
    TableCell,
    TableRow,
    CircularProgress
} from '@material-ui/core/'
import { authFetch, logout } from '../../auth'

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.secondary.main,
        padding: 6,
        fontWeight: 'bold'
    },
    body: {
        fontSize: 14,
        color: theme.palette.primary.dark,
        padding: 8,
        border: `1px solid ${theme.palette.secondary.dark}`
    },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        '&:nth-of-type(even)': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))(TableRow);

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        padding: theme.spacing(2)
    },
    sectionTitle: {
        marginBottom: 12,
        padding: theme.spacing(1),
        backgroundColor: theme.palette.secondary.light,
        borderRadius: 3,
        color: theme.palette.primary.dark,
        textAlign: 'center',
        fontWeight: 'bold'
    },
    tableWrapper: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3)
    },
    highlightCellCrpui: {
        fontWeight: 'bold !important',
        backgroundColor: `${theme.palette.secondary.light} !important`,
        color: `${theme.palette.primary.dark} !important`,
        border: `1px solid ${theme.palette.secondary.dark} !important`
    },
    highlightCellPui: {
        fontWeight: 'bold !important',
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: `${theme.palette.common.white} !important`,
        border: `1px solid ${theme.palette.primary.dark} !important`
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(4)
    }
}));

export default function CrpuiCalculatorTable(props) {
    const classes = useStyles();
    const [crpuiData, setCrpuiData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const tablePrintRef = useRef();

    useEffect(() => {
        if (!props.periodDate || !props.tariffCalcVersion || !props.market || !props.username) {
            return;
        }

        document.title = "Liquidación CRPUI y PUI - Res. CREG 101 121";
        setLoading(true);
        setErrorMsg('');

        // Determinación del año y periodo según la convención de corte tarifario
        const periodMonth = props.periodDate.getMonth() + 1;
        const year = periodMonth < 12 ? props.periodDate.getFullYear() : props.periodDate.getFullYear() + 1;
        const period = periodMonth < 12 ? periodMonth + 1 : 1;
        const version = props.tariffCalcVersion;
        const usernameCode = props.username.slice(0, 3);
        const electricTariffId = String(usernameCode) + String(year) + String(period) + String(version);

        const url = `/api/calculate-crpui?electric_tariff_id=${electricTariffId}&market=${encodeURIComponent(props.market)}&period=${period}&year=${year}`;

        authFetch(url)
            .then(response => {
                if (response.status === 422) {
                    logout();
                    return null;
                }
                return response.json();
            })
            .then(data => {
                setLoading(false);
                if (data && !data.error) {
                    setCrpuiData(data);
                } else if (data && data.error) {
                    setErrorMsg(data.error);
                    setCrpuiData(null);
                }
            })
            .catch(err => {
                setLoading(false);
                setErrorMsg('Error de conexión al obtener liquidación CRPUI');
                setCrpuiData(null);
            });

    }, [props.periodDate, props.tariffCalcVersion, props.market, props.username]);

    const buildTableRows = (data) => {
        const inp = data.extracted_inputs || {};
        const cu = inp.cu_components_m1 || {};
        const calc = data.calculations || {};
        const pInfo = data.period_info || {};

        const rcpuiVal = Number(inp.rcpui_jt || 0);
        const vpuiVal = Number(inp.vpui_m1 || 0);
        const vrVal = Number(inp.vr_m1 || 0);
        const gVal = Number(cu.G || 0);
        const tVal = Number(cu.T || 0);
        const d1Val = Number(cu.D1 || 0);
        const pr1Val = Number(cu.PR1 || 0);
        const rVal = Number(cu.R || 0);
        const propVal = Number(calc.sales_ratio_prop || 0);
        const sumCuVal = Number(calc.sum_cu_components || 0);
        const vrCniorVal = Number(inp.vr_cnior_m2 || 0);
        const crpuiFinal = Number(calc.crpui_ij_m || 0);
        const puiFinal = Number(calc.pui_j_m || 0);

        return [
            // Sección 1: Insumos Art. 10
            { 
                category: 'Insumos Base', 
                symbol: 'RCPUI j,t (= RCAE j,t)', 
                desc: 'Prima de riesgo de cartera en áreas especiales', 
                unit: '%', 
                value: `${(rcpuiVal * 100).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}%`,
                highlightType: null
            },
            { 
                category: 'Insumos Base', 
                symbol: 'VPUI i,j,m-1 (VAE)', 
                desc: 'Ventas a usuarios en áreas especiales (m-1)', 
                unit: 'kWh', 
                value: vpuiVal.toLocaleString('en-US', { minimumFractionDigits: 2 }),
                highlightType: null
            },
            { 
                category: 'Insumos Base', 
                symbol: 'VR i,j,m-1 (VRC)', 
                desc: 'Ventas totales a usuarios regulados (m-1)', 
                unit: 'kWh', 
                value: vrVal.toLocaleString('en-US', { minimumFractionDigits: 2 }),
                highlightType: null
            },
            
            // Componentes CU m-1
            { 
                category: 'Componentes CU (m-1)', 
                symbol: 'G i,j,m-1', 
                desc: 'Costo de compra de energía', 
                unit: '$/kWh', 
                value: gVal.toLocaleString('en-US', { minimumFractionDigits: 6 }),
                highlightType: null
            },
            { 
                category: 'Componentes CU (m-1)', 
                symbol: 'T m-1', 
                desc: 'Costo por uso del STN', 
                unit: '$/kWh', 
                value: tVal.toLocaleString('en-US', { minimumFractionDigits: 6 }),
                highlightType: null
            },
            { 
                category: 'Componentes CU (m-1)', 
                symbol: 'D1,j,m-1', 
                desc: 'Costo de distribución Nivel 1', 
                unit: '$/kWh', 
                value: d1Val.toLocaleString('en-US', { minimumFractionDigits: 6 }),
                highlightType: null
            },
            { 
                category: 'Componentes CU (m-1)', 
                symbol: 'PR1,j,m-1', 
                desc: 'Costo pérdidas acumuladas Nivel 1', 
                unit: '$/kWh', 
                value: pr1Val.toLocaleString('en-US', { minimumFractionDigits: 6 }),
                highlightType: null
            },
            { 
                category: 'Componentes CU (m-1)', 
                symbol: 'R i,m-1', 
                desc: 'Costo de restricciones', 
                unit: '$/kWh', 
                value: rVal.toLocaleString('en-US', { minimumFractionDigits: 6 }),
                highlightType: null
            },
            
            // Cálculos intermedios
            { 
                category: 'Cálculos Intermedios', 
                symbol: 'Prop. VPUI', 
                desc: 'Proporción ventas áreas especiales (VPUI / VR)', 
                unit: 'Fracción', 
                value: propVal.toLocaleString('en-US', { minimumFractionDigits: 6 }),
                highlightType: null
            },
            { 
                category: 'Cálculos Intermedios', 
                symbol: 'Σ CU (sin C)', 
                desc: 'Suma componentes CU (G + T + D1 + PR1 + R)', 
                unit: '$/kWh', 
                value: sumCuVal.toLocaleString('en-US', { minimumFractionDigits: 6 }),
                highlightType: null
            },

            // Insumo traslado Art. 11
            { 
                category: 'Traslado Art. 11', 
                symbol: 'VR i,j,m-2', 
                desc: `Ventas reguladas CNIOR (Periodo ${pInfo.target_period_m2 || '-'}-${pInfo.target_year_m2 || '-'})`, 
                unit: 'kWh', 
                value: vrCniorVal.toLocaleString('en-US', { minimumFractionDigits: 2 }),
                highlightType: null
            },

            // Resultados Finales
            { 
                category: 'Resultados Finales', 
                symbol: 'CRPUI i,j,m', 
                desc: 'Costo prestación del servicio PUI en áreas especiales', 
                unit: '$/kWh', 
                value: crpuiFinal.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 }), 
                highlightType: 'crpui' 
            },
            { 
                category: 'Resultados Finales', 
                symbol: 'PUI j,m', 
                desc: 'Valor total a trasladar a usuarios regulados (mes m)', 
                unit: '$ COP', 
                value: `$ ${puiFinal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
                highlightType: 'pui' 
            }
        ];
    };

    const getCellClass = (highlightType) => {
        if (highlightType === 'crpui') return classes.highlightCellCrpui;
        if (highlightType === 'pui') return classes.highlightCellPui;
        return null;
    };

    return (
        <div className={classes.root}>
            <Grid container justify="center">
                <Zoom in timeout={700}>
                    <Grid item xs={12} md={10}>
                        {props.tariffCalcVersion === '' ? (
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                                No se ha seleccionado una versión de cálculo para el periodo actual.
                            </Typography>
                        ) : loading ? (
                            <div className={classes.loadingContainer}>
                                <CircularProgress />
                            </div>
                        ) : errorMsg ? (
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                                {errorMsg}
                            </Typography>
                        ) : crpuiData ? (
                            <div ref={tablePrintRef} className={classes.tableWrapper}>
                                <Typography variant="subtitle1" className={classes.sectionTitle}>
                                    Cálculo CRPUI y Valor PUI — Res. CREG 101 121 de 2026 (Mercado: {crpuiData.market})
                                </Typography>
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell align="center" style={{ width: '18%' }}>Etapa / Categoría</StyledTableCell>
                                                <StyledTableCell align="center" style={{ width: '20%' }}>Variable</StyledTableCell>
                                                <StyledTableCell align="left" style={{ width: '38%' }}>Descripción</StyledTableCell>
                                                <StyledTableCell align="center" style={{ width: '10%' }}>Unidad</StyledTableCell>
                                                <StyledTableCell align="right" style={{ width: '14%' }}>Valor</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {buildTableRows(crpuiData).map((row, idx) => {
                                                const cellClass = getCellClass(row.highlightType);
                                                return (
                                                    <StyledTableRow key={idx}>
                                                        <StyledTableCell align="center">{row.category}</StyledTableCell>
                                                        <StyledTableCell 
                                                            align="center" 
                                                            style={{ fontWeight: row.highlightType ? 'bold' : 'normal' }}
                                                        >
                                                            {row.symbol}
                                                        </StyledTableCell>
                                                        <StyledTableCell align="left">{row.desc}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.unit}</StyledTableCell>
                                                        <StyledTableCell 
                                                            align="right" 
                                                            className={cellClass}
                                                        >
                                                            {row.value}
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        ) : (
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                                No hay información disponible de CRPUI para los parámetros seleccionados.
                            </Typography>
                        )}
                    </Grid>
                </Zoom>
            </Grid>
        </div>
    );
}